import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { describeError, getRateLimitRejection } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { CrmEmailPacePanel } from "./CrmEmailPacePanel";
import { MinVersionPanel } from "./MinVersionPanel";
import { NearbyAlertsField } from "./NearbyAlertsField";
import { RecaptchaPanel } from "./RecaptchaPanel";
import { NEARBY_ALERTS_SETTINGS } from "./appConfig.queries";
import { UPDATE_NEARBY_ALERTS_CONFIG } from "./appConfig.mutation";
import {
  Draft,
  NearbyAlertsSettings,
  PROPAGATION_NOTICE,
  QUIET_HOURS_NOTICE,
  buildPatch,
  changedFields,
  fieldErrorOf,
  formBounds,
  toDraft,
  validateDraft,
} from "./nearbyAlerts";

export const AppConfig: React.FC = (): JSX.Element => {
  const { data, loading, error, refetch } = useQuery(NEARBY_ALERTS_SETTINGS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  /**
   * The mutation returns the whole settings object, so a save needs no re-read
   * — but `NearbyAlertsSettings` has no id, so the cache files the result under
   * the mutation's own root field and the query's copy stays untouched. Without
   * this the form would snap back to the pre-save values and read as a failure.
   */
  const [updateConfig] = useMutation(UPDATE_NEARBY_ALERTS_CONFIG, {
    update(cache, { data: result }) {
      if (!result?.updateNearbyAlertsConfig) return;

      cache.writeQuery({
        query: NEARBY_ALERTS_SETTINGS,
        data: { nearbyAlertsSettings: result.updateNearbyAlertsConfig },
      });
    },
  });

  const toast = useToast();

  /**
   * Only what the admin typed — not the whole form.
   *
   * The displayed draft is derived from this over the stored config during
   * render, rather than copied into state by an effect. An effect would leave
   * every input blank for the first paint after each response, and would have
   * to be re-run on refresh and after every save to avoid showing stale values.
   */
  const [edits, setEdits] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const settings: NearbyAlertsSettings | undefined = data?.nearbyAlertsSettings;
  const bounds = useMemo(() => formBounds(settings?.bounds ?? []), [settings]);

  const draft: Draft = useMemo(
    () => ({ ...toDraft(settings?.config ?? {}, bounds), ...edits }),
    [settings, bounds, edits]
  );

  const dirty = useMemo(
    () =>
      settings ? changedFields(draft, settings.config, bounds).length > 0 : false,
    [draft, settings, bounds]
  );

  const handleChange = useCallback((field: string, value: string) => {
    setEdits((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Saves only the fields that changed.
   *
   * Validated locally first against the server's own bounds — that is a round
   * trip saved, not a second opinion. A rejection is attached to the input it
   * names rather than shown as a banner, so the admin does not have to work out
   * which of fifteen numbers the server meant.
   */
  const handleSave = useCallback(async () => {
    if (!settings) return;

    /*
     * Only the fields being sent are validated, not every input on screen.
     *
     * A bound can arrive for a field the query does not select yet — that is
     * how a server-added field shows up here without a dashboard release — and
     * its input then renders empty because no stored value reached it.
     * Validating the whole form would call that empty input "Required" and
     * block every unrelated save until this screen shipped again.
     */
    const changed = changedFields(draft, settings.config, bounds);
    const localErrors = validateDraft(
      draft,
      bounds.filter((bound) => changed.includes(bound.field))
    );

    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      return;
    }

    setSaving(true);

    try {
      await updateConfig({
        variables: { input: buildPatch(draft, settings.config, bounds) },
      });

      setEdits({});
      setErrors({});
      toast.success(`Saved. ${PROPAGATION_NOTICE}`);
    } catch (mutationError) {
      const fieldError = fieldErrorOf(mutationError);

      if (fieldError) {
        setErrors({ [fieldError.field]: fieldError.message });
        return;
      }

      // A rate-limit rejection is announced by the global Apollo error link
      // already, in the same words; toasting it again would double it.
      if (!getRateLimitRejection(mutationError)) {
        toast.error(describeError(mutationError, "Could not save the tuning"));
      }
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, settings, bounds, updateConfig]);

  const handleReset = useCallback(() => {
    setEdits({});
    setErrors({});
  }, []);


  /**
   * The tuning form's own states, rendered in place of the tuning card rather
   * than in place of the screen.
   *
   * They used to be early returns. They cannot be: the forced-upgrade control
   * below is the thing somebody reaches for during an incident, and a failed
   * `nearbyAlertsSettings` read — a different query, against a different row —
   * is no reason for it to vanish.
   */
  const tuningState = (() => {
    if (loading && !data) return "loading";
    if (error) return "error";

    return settings ? "ready" : "empty";
  })();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* ---------------------------------------------- Header */}
        <Grid item xs={12}>
          <Item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
                sx={{ m: 2 }}
              >
                <Typography variant="h4">App Config</Typography>
                {settings && (
                  <Chip
                    color={settings.effectiveEnabled ? "success" : "error"}
                    label={
                      settings.effectiveEnabled
                        ? "Nearby check-ins running"
                        : "Nearby check-ins off"
                    }
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ m: 2 }}>
                {settings?.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Last changed{" "}
                    {new Date(settings.updatedAt).toLocaleString()}
                  </Typography>
                )}

                {/*
                 * Named for what it re-reads. The forced-upgrade card has its
                 * own refresh, against its own query, and two buttons both
                 * saying "Refresh" would each look like they cover the page.
                 */}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  onClick={() => refetch()}
                >
                  {loading ? "Refreshing…" : "Refresh tuning"}
                </Button>
              </Stack>
            </Grid>
          </Item>
        </Grid>

        {/* ---------------------------------------------- Forced app update */}
        <Grid item xs={12}>
          <MinVersionPanel />
        </Grid>

        {/* ---------------------------------------------- reCAPTCHA score */}
        <Grid item xs={12}>
          <RecaptchaPanel />
        </Grid>

        {/* ---------------------------------------------- Campaign email pace */}
        <Grid item xs={12}>
          <CrmEmailPacePanel />
        </Grid>

        {/* ---------------------------------------------- Where things live */}
        <Grid item xs={12}>
          <Alert severity="info">
            <AlertTitle>Nearby check-in tuning</AlertTitle>
            <Typography variant="body2">
              These numbers steer the mobile app's geofencing — the radius it
              watches, how long someone has to stay put, and how often it may
              notify them. <strong>Nothing on the server reads them</strong>;
              the app fetches them at launch and falls back to its compiled
              defaults per field, so a bad row cannot break tracking.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Rate limits live on{" "}
              <Link component={RouterLink} to="/rate-limits">
                Rate Limits
              </Link>{" "}
              and country blocking on{" "}
              <Link component={RouterLink} to="/country-blocking">
                Country Blocking
              </Link>
              , each with their own screen.
            </Typography>
          </Alert>
        </Grid>

        {tuningState === "loading" && (
          <Grid item xs={12}>
            <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading app config…
              </Typography>
            </Stack>
          </Grid>
        )}

        {tuningState === "error" && (
          <Grid item xs={12}>
            <Item>
              <ErrorNotifier error={error} />
            </Item>
          </Grid>
        )}

        {tuningState === "empty" && (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
              No app config returned.
            </Typography>
          </Grid>
        )}

        {/* ---------------------------------------------- Warnings */}
        {settings && settings.warnings.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Saved, but worth a look</AlertTitle>
              {settings.warnings.map((warning) => (
                <Typography key={warning} variant="body2">
                  {warning}
                </Typography>
              ))}
            </Alert>
          </Grid>
        )}

        {settings?.usingDefaults && (
          <Grid item xs={12}>
            <Alert severity="info">
              No stored tuning row exists yet, so these are the app's compiled
              defaults. Saving anything here creates the row; until then the app
              behaves exactly as it always has.
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- Tuning */}
        {settings && (
          <Grid item xs={12}>
            <Item sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                Tuning
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ranges, units and defaults come from the server, so this form
                cannot drift out of step with what the API will accept. Values are
                rejected rather than clamped — a number shown here is the number
                in effect.
              </Typography>

              {/*
               * One input per bound, in payload order. No section headings: the
               * only nested group the API returns is the per-level notification
               * budgets, and those are not edited here — see `formBounds`.
               */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {bounds.map((bound) => (
                  <Grid item xs={12} sm={6} md={4} key={bound.field}>
                    <NearbyAlertsField
                      bound={bound}
                      value={draft[bound.field] ?? ""}
                      error={errors[bound.field]}
                      disabled={saving}
                      onChange={handleChange}
                    />
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mb: 2 }}>
                {QUIET_HOURS_NOTICE}
              </Alert>

              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  disabled={!dirty || saving}
                  onClick={handleSave}
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>

                <Button disabled={!dirty || saving} onClick={handleReset}>
                  Discard
                </Button>

                {dirty && (
                  <Typography variant="caption" color="text.secondary">
                    Only the fields you changed are sent.
                  </Typography>
                )}
              </Stack>
            </Item>
          </Grid>
        )}

        {/* ---------------------------------------------- Propagation */}
        {settings && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Changes are not instant</AlertTitle>
              <Typography variant="body2">
                The app reads config once per launch, so an edit reaches a member
                at their <strong>next cold start</strong> — not on resume. Values
                that change what the OS is monitoring then take hold at the next
                scan. Worst case: {PROPAGATION_NOTICE}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Turning tracking off does not recall notifications already
                scheduled on a device that has not launched since.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
